import { NotificationSchedule as NotificationScheduleEnum } from '@gripp/shared-logic';
import { useMemo, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, Text, View } from 'react-native';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { Colors } from '../../themes/colors';
import { CheckboxOption } from '../crop-circle/Components/CheckboxOption';
import { tokens } from '../crop-circle/tokens';
import { preferencesStyles } from './styles';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { useThemeMode } from '../../themes/useThemeMode';

type NotificationScheduleProps = {
  control: Control<any>;
  disabled?: boolean;
};

export const NotificationSchedule = ({
  control,
  disabled,
}: NotificationScheduleProps) => {
  const scheduleDaysName = 'notificationPreferences.schedule.scheduleDays';
  const startTimeName = 'notificationPreferences.schedule.startTime';
  const endTimeName = 'notificationPreferences.schedule.endTime';

  const { t } = useTranslation();

  const scheduleDaysOptions = useMemo(
    () => [
      {
        label: t('preferences.notifications.schedule.weekDays') || 'Week Days',
        value: NotificationScheduleEnum.WeekDays,
      },
      {
        label: t('preferences.notifications.schedule.everyDay') || 'Every Day',
        value: NotificationScheduleEnum.EveryDay,
      },
    ],
    [t]
  );

  const startTime = useWatch({ control, name: startTimeName });
  const endTime = useWatch({ control, name: endTimeName });

  const getTimeString = (date: Date): string => {
    return date
      ? new Date(date).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('preferences.notifications.schedule.notSet');
  };

  return (
    <View style={preferencesStyles.contentContainer}>
      <View>
        <Text style={preferencesStyles.sectionTitle}>SCHEDULE</Text>
        <View style={styles.rows}>
          {/* Weekday / Every Day radio rows */}
          <Controller
            control={control}
            name={scheduleDaysName}
            render={({ field: { onChange, value } }) => (
              <>
                {scheduleDaysOptions.map((option) => {
                  const selected = option.value === value;
                  return (
                    <Pressable
                      key={option.value}
                      style={styles.row}
                      onPress={() => !disabled && onChange(option.value)}
                      disabled={disabled}
                      accessibilityRole="radio"
                      accessibilityState={{ selected, disabled: !!disabled }}
                    >
                      <Text style={styles.rowLabel}>{option.label}</Text>
                      <View pointerEvents="none">
                        <CheckboxOption
                          mode="radio"
                          state={selected ? 'checked' : 'unchecked'}
                          disabled={disabled}
                          backgroundColor="transparent"
                        />
                      </View>
                    </Pressable>
                  );
                })}
              </>
            )}
          />

          {/* Start Time */}
          <TimeInputRow
            control={control}
            name={startTimeName}
            label={t('preferences.notifications.schedule.startTime')}
            defaultHours={9}
            defaultMinutes={0}
            disabled={disabled}
            getTimeString={getTimeString}
            timeValue={startTime}
          />

          {/* End Time */}
          <TimeInputRow
            control={control}
            name={endTimeName}
            label={t('preferences.notifications.schedule.endTime')}
            defaultHours={17}
            defaultMinutes={0}
            disabled={disabled}
            getTimeString={getTimeString}
            timeValue={endTime}
          />
        </View>
      </View>
    </View>
  );
};

type TimeInputRowProps = {
  control: Control<any>;
  name: string;
  label: string;
  defaultHours: number;
  defaultMinutes: number;
  disabled?: boolean;
  getTimeString: (date: Date) => string;
  timeValue: Date | undefined;
};

const TimeInputRow = ({
  control,
  name,
  label,
  defaultHours,
  defaultMinutes,
  disabled,
  getTimeString,
  timeValue,
}: TimeInputRowProps) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  // See DatePickerWheel: the picker follows the device scheme unless told the
  // app's mode, and the two diverge whenever the app is not set to System.
  const themeMode = useThemeMode();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const hasTime = value || timeValue;
        const timeString = getTimeString(value || timeValue);

        return (
          <View style={styles.row}>
            <View style={styles.rowLabelGroup}>
              <Text style={styles.rowLabel}>{label}</Text>
              {hasTime && <Text style={styles.rowValue}>{timeString}</Text>}
            </View>
            <Pressable
              onPress={() => setVisible(true)}
              disabled={disabled}
              style={[styles.setPill, disabled && styles.setPillDisabled]}
              accessibilityRole="button"
              accessibilityLabel={t('preferences.notifications.schedule.set')}
            >
              <Text style={styles.setPillText}>
                {t('preferences.notifications.schedule.set')}
              </Text>
            </Pressable>
            <DateTimePickerModal
              textColor={Colors.role.textSecondary}
              isDarkModeEnabled={themeMode === 'dark'}
              themeVariant={themeMode}
              cancelTextIOS={t('preferences.notifications.schedule.cancel')}
              confirmTextIOS={t('preferences.notifications.schedule.accept')}
              isVisible={visible}
              mode="time"
              display="spinner"
              date={
                value
                  ? new Date(value)
                  : new Date(1970, 0, 1, defaultHours, defaultMinutes)
              }
              onConfirm={(date) => {
                setVisible(false);
                onChange(date);
              }}
              onCancel={() => setVisible(false)}
            />
          </View>
        );
      }}
    />
  );
};

const styles = themedStyleSheet((role) => ({
  rows: {
    gap: tokens.spacing.xs,
  },
  // Each schedule row: light-gray rounded bar, label on the left, control on
  // the right — matches the V6 Figma (node 3962-46495).
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 40,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.sm,
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: role.surfaceHover,
  },
  rowLabelGroup: {
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  rowLabel: {
    ...tokens.typography.label.large,
    color: role.textStrong,
  },
  rowValue: {
    ...tokens.typography.label.large,
    color: role.textPrimary,
  },
  setPill: {
    height: 24,
    borderRadius: tokens.borderRadius.pill,
    paddingHorizontal: tokens.spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: role.surfaceInverse,
  },
  setPillDisabled: {
    opacity: 0.6,
  },
  setPillText: {
    fontSize: 12,
    lineHeight: 14,
    fontWeight: '400',
    letterSpacing: 0.2,
    color: role.textOnInverse,
  },
}));
